import { documentKind, formatDocument, onlyDigits } from '@talkium/types';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { auth } from '../../api/resources.js';
import { session } from '../../api/client.js';
import { Button } from '../../components/ui/button.js';
import { Field, Input, inputClass } from '../../components/ui/input.js';
import { COUNTRY_CODES } from '../../data/country-codes.js';
import { useAction } from '../../lib/hooks.js';
import { AuthError, AuthLayout } from './layout.js';

const COUNTRIES: Array<{ code: string; name: string }> = [
  { code: 'BR', name: 'Brasil' },
  { code: 'PT', name: 'Portugal' },
  { code: 'US', name: 'Estados Unidos' },
  { code: 'AR', name: 'Argentina' },
  { code: 'MX', name: 'México' },
  { code: 'CL', name: 'Chile' },
  { code: 'CO', name: 'Colômbia' },
  { code: 'PE', name: 'Peru' },
  { code: 'UY', name: 'Uruguai' },
  { code: 'PY', name: 'Paraguai' },
  { code: 'ES', name: 'Espanha' },
  { code: 'IT', name: 'Itália' },
  { code: 'GB', name: 'Reino Unido' },
  { code: 'DE', name: 'Alemanha' },
  { code: 'FR', name: 'França' },
  { code: 'AO', name: 'Angola' },
  { code: 'MZ', name: 'Moçambique' },
];

/**
 * Cadastro (PRD §17; docs/mega-panel.md, gate de cadastro): conta, organização e projeto
 * nascem juntos. País e CPF/CNPJ entram aqui porque a cobrança precisa deles depois, e
 * pedir na hora de pagar é pedir na pior hora.
 */
export function SignupPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
    organizationName: '',
    country: 'BR',
    document: '',
  });
  const [documentError, setDocumentError] = useState<string | null>(null);

  const set =
    (key: keyof typeof form) => (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setForm((current) => ({ ...current, [key]: event.target.value }));

  const submit = useAction(async () => {
    const digits = onlyDigits(form.document);
    if (form.country === 'BR') {
      if (!digits) {
        setDocumentError('Informe o CPF ou CNPJ.');
        return;
      }
      if (!documentKind(digits)) {
        setDocumentError('CPF ou CNPJ inválido. Confira os dígitos.');
        return;
      }
    }
    setDocumentError(null);
    const result = await auth.signup({
      name: form.name,
      email: form.email,
      password: form.password,
      organizationName: form.organizationName,
      country: form.country,
      ...(digits ? { document: digits } : {}),
    });
    session.set(result.token, result.organization.id);
    navigate('/verify-email', { replace: true });
  });

  const isBrazil = form.country === 'BR';

  return (
    <AuthLayout
      title="Criar conta"
      description="10 dias grátis com uma instância. Sem cartão para começar."
      footer={
        <>
          Já tem conta? <Link to="/login">Entrar</Link>
        </>
      }
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          void submit.run();
        }}
      >
        <AuthError error={submit.error} />
        <Field label="Seu nome" htmlFor="name">
          <Input
            id="name"
            required
            minLength={2}
            autoComplete="name"
            value={form.name}
            onChange={set('name')}
          />
        </Field>
        <Field label="E-mail" htmlFor="email" hint="Você recebe um código para confirmar.">
          <Input
            id="email"
            type="email"
            required
            autoComplete="email"
            value={form.email}
            onChange={set('email')}
          />
        </Field>
        <Field
          label="Senha"
          htmlFor="password"
          hint="Mínimo de 12 caracteres, com letras e números."
        >
          <Input
            id="password"
            type="password"
            required
            minLength={12}
            autoComplete="new-password"
            value={form.password}
            onChange={set('password')}
          />
        </Field>
        <Field label="Nome da empresa" htmlFor="organizationName">
          <Input
            id="organizationName"
            required
            minLength={2}
            autoComplete="organization"
            value={form.organizationName}
            onChange={set('organizationName')}
          />
        </Field>
        <div className="grid grid-cols-[minmax(0,2fr)_minmax(0,3fr)] gap-3">
          <Field label="País" htmlFor="country">
            <select
              id="country"
              className={inputClass}
              value={form.country}
              onChange={set('country')}
            >
              {COUNTRIES.map((country) => (
                <option key={country.code} value={country.code}>
                  {COUNTRY_CODES.find((c) => c.name.startsWith(country.name.split(' ')[0]!))
                    ?.flag ?? ''}{' '}
                  {country.name}
                </option>
              ))}
            </select>
          </Field>
          <Field
            label={isBrazil ? 'CPF ou CNPJ' : 'Documento fiscal (opcional)'}
            htmlFor="document"
            {...(documentError ? { hint: documentError } : {})}
          >
            <Input
              id="document"
              inputMode="numeric"
              required={isBrazil}
              placeholder={isBrazil ? '000.000.000-00' : ''}
              value={form.document}
              onChange={(event) => {
                setDocumentError(null);
                const raw = event.target.value;
                setForm((current) => ({
                  ...current,
                  document: isBrazil ? formatDocument(onlyDigits(raw).slice(0, 14)) : raw,
                }));
              }}
              aria-invalid={documentError ? true : undefined}
            />
          </Field>
        </div>
        <p className="mt-1 mb-4 text-xs text-subtle">
          Ao criar a conta você concorda com os termos de uso e a política de privacidade.
        </p>
        <Button className="w-full" type="submit" disabled={submit.pending}>
          {submit.pending ? 'Criando…' : 'Criar conta grátis'}
        </Button>
      </form>
    </AuthLayout>
  );
}
